import { useEffect, useRef, useState, type ComponentType } from 'react'
import { Link, useLocation } from '@tanstack/react-router'
import {
  ChevronDown,
  Sparkles,
  HeartPulse,
  PauseCircle,
  AlertTriangle,
  ShieldHalf,
  LifeBuoy,
  Search,
  FileText,
  Library,
  KeyRound,
  HeartHandshake,
  Lock,
  ArrowRight,
  Menu,
  X,
} from 'lucide-react'

type IconCmp = ComponentType<{ className?: string }>

/** Same items as the NASAA SiteHeader, themed navy/gold for the AMBA build. */
const BASICS: { slug: string; label: string; icon: IconCmp }[] = [
  { slug: 'new-age-of-scams', label: 'The New Age of Scams', icon: Sparkles },
  { slug: 'emotional-hijack', label: 'The Emotional Hijack', icon: HeartPulse },
  { slug: 'pause-and-verify', label: 'Pause and Verify', icon: PauseCircle },
  { slug: 'warning-signs', label: 'Spot the Warning Signs', icon: AlertTriangle },
  { slug: 'digital-self-defense', label: 'Digital Self-Defense', icon: ShieldHalf },
  { slug: 'if-youre-scammed', label: 'If You’re Scammed', icon: LifeBuoy },
]

const TOOLS: { to: string; label: string; icon: IconCmp; locked?: boolean }[] = [
  { to: '/tools/scam-checker', label: 'Scam Checker', icon: Search, locked: true },
  { to: '/articles', label: 'Scam Prevention Articles', icon: FileText },
  { to: '/scam-library', label: 'Scam Library', icon: Library },
  { to: '/tools/password-check', label: 'Password Check', icon: KeyRound, locked: true },
  { to: '/tools/charity-checker', label: 'Charity Checker', icon: HeartHandshake, locked: true },
]

export default function AmbaHeader() {
  const [openMenu, setOpenMenu] = useState<'basics' | 'tools' | null>(null)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const ref = useRef<HTMLElement>(null)
  const { pathname } = useLocation()
  const isHome = pathname === '/'

  // On the homepage the header overlays the hero video and starts transparent,
  // turning solid teal once you scroll past the top (or open the mobile menu).
  // Every other page keeps the normal solid sticky header.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [pathname])

  const solid = !isHome || scrolled || mobileOpen

  useEffect(() => {
    if (!openMenu) return
    const onClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpenMenu(null)
    }
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpenMenu(null)
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [openMenu])

  return (
    <header
      ref={ref}
      className={`${isHome ? 'fixed inset-x-0' : 'sticky'} top-0 z-40 border-b transition-colors duration-300 ${
        solid ? 'border-white/10 bg-[#253a3f]' : 'border-transparent bg-transparent'
      }`}
    >
      <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 px-6 py-4">
        <Link to="/" className="shrink-0">
          {/* AMBA logo is a white SVG, sits on the teal header. */}
          <img
            src="/amba-logo.svg"
            alt="Association of Military Banks of America"
            className="h-8 w-auto sm:h-10"
          />
        </Link>

        <nav className="order-3 flex w-full items-center justify-center gap-1 sm:gap-4 lg:order-none lg:w-auto">
          <NavMenu
            label="Scam Defense Basics"
            isOpen={openMenu === 'basics'}
            onToggle={() => setOpenMenu(openMenu === 'basics' ? null : 'basics')}
            onItemClick={() => setOpenMenu(null)}
            items={BASICS.map(b => ({ to: `/basics/${b.slug}`, label: b.label, icon: b.icon }))}
            widthClass="w-[17rem]"
          />
          <NavMenu
            label="Tools & Resources"
            isOpen={openMenu === 'tools'}
            onToggle={() => setOpenMenu(openMenu === 'tools' ? null : 'tools')}
            onItemClick={() => setOpenMenu(null)}
            items={TOOLS.map(t => ({ to: t.to, label: t.label, icon: t.icon, locked: t.locked }))}
          />
          <Link
            to="/about"
            className="cursor-pointer rounded-md px-3 py-2 text-base font-semibold text-white/90 transition hover:text-white"
          >
            About
          </Link>
        </nav>

        <div className="flex shrink-0 items-center gap-2">
          <Link
            to="/basics/$basicsSlug"
            params={{ basicsSlug: 'new-age-of-scams' }}
            className="hidden items-center gap-2 rounded-lg bg-[#42817a] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#386f69] sm:inline-flex"
          >
            Start the course
            <ArrowRight className="h-4 w-4" />
          </Link>
          <button
            type="button"
            onClick={() => setMobileOpen(v => !v)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-lg text-white transition hover:bg-white/10 lg:hidden"
          >
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="border-t border-white/10 bg-[#253a3f] lg:hidden">
          <nav className="mx-auto flex max-w-7xl flex-col gap-1 px-6 py-4">
            <p className="px-1 pt-2 text-xs font-semibold uppercase tracking-wide text-[#d9bd76]">
              Scam Defense Basics
            </p>
            {BASICS.map(b => (
              <Link
                key={b.slug}
                to="/basics/$basicsSlug"
                params={{ basicsSlug: b.slug }}
                onClick={() => setMobileOpen(false)}
                className="rounded-lg px-3 py-2.5 text-[15px] font-medium text-white/80 transition hover:bg-white/5"
              >
                {b.label}
              </Link>
            ))}
            <div className="my-2 border-t border-white/10" />
            <Link to="/scam-library" onClick={() => setMobileOpen(false)} className="rounded-lg px-3 py-2.5 text-[15px] font-medium text-white/80 transition hover:bg-white/5">
              Scam Library
            </Link>
            <Link to="/articles" onClick={() => setMobileOpen(false)} className="rounded-lg px-3 py-2.5 text-[15px] font-medium text-white/80 transition hover:bg-white/5">
              Scam Prevention Articles
            </Link>
            <Link to="/about" onClick={() => setMobileOpen(false)} className="rounded-lg px-3 py-2.5 text-[15px] font-medium text-white/80 transition hover:bg-white/5">
              About
            </Link>
            <Link
              to="/basics/$basicsSlug"
              params={{ basicsSlug: 'new-age-of-scams' }}
              onClick={() => setMobileOpen(false)}
              className="mt-3 inline-flex items-center justify-center gap-2 rounded-lg bg-[#42817a] px-4 py-3 text-sm font-semibold text-white"
            >
              Start the course
              <ArrowRight className="h-4 w-4" />
            </Link>
          </nav>
        </div>
      )}
    </header>
  )
}

function NavMenu({
  label,
  isOpen,
  onToggle,
  onItemClick,
  items,
  widthClass = 'w-[22rem]',
}: {
  label: string
  isOpen: boolean
  onToggle: () => void
  onItemClick: () => void
  items: { to: string; label: string; icon: IconCmp; locked?: boolean }[]
  widthClass?: string
}) {
  return (
    <div className="relative">
      <button
        onClick={onToggle}
        className="flex cursor-pointer items-center gap-1.5 rounded-md px-3 py-2 text-base font-semibold text-white/90 transition hover:text-white"
      >
        {label}
        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className={`absolute left-1/2 z-50 mt-3 -translate-x-1/2 rounded-2xl bg-white p-2 text-slate-700 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.45)] ring-1 ring-slate-200/70 ${widthClass}`}>
          <ul>
            {items.map(item => {
              const Icon = item.icon
              if (item.locked) {
                return (
                  <li key={item.to}>
                    <div
                      aria-disabled="true"
                      title="Coming soon"
                      className="flex cursor-not-allowed items-center gap-3.5 rounded-xl px-3 py-1.5 text-[15px] font-medium text-slate-400"
                    >
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center text-slate-300">
                        <Icon className="h-5 w-5" />
                      </span>
                      <span className="flex-1 leading-tight">{item.label}</span>
                      <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-500">
                        <Lock className="h-2.5 w-2.5" />
                        Soon
                      </span>
                    </div>
                  </li>
                )
              }
              return (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    onClick={onItemClick}
                    className="group flex items-center gap-3.5 rounded-xl px-3 py-1.5 text-[15px] font-medium text-slate-700 transition hover:bg-[#c8a04b]/15 hover:text-slate-900"
                  >
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center text-[#8a6a1c]">
                      <Icon className="h-5 w-5" />
                    </span>
                    <span className="flex-1 leading-tight">{item.label}</span>
                  </Link>
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}
